/** @file previewSchedule: a trailing debounce for the post-commit refresh; runs never overlap. */

/** The timer pair the schedule uses (default the globals; tests pass fakes). */
export type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

/** `schedule()` resolves when the run that covers this call has finished; `cancel()` drops a waiting run. */
export type PreviewSchedule = { schedule: () => Promise<void>; cancel: () => void };

const globalTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => { globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>); },
};

/**
 * Calls within `delayMs` of each other share one `run` (trailing edge); a run that starts while another is in progress
 * waits for it. `run` should catch its own errors; a throw still settles the waiters. `cancel` resolves the waiters
 * of the dropped run so nobody hangs on a closed puppet.
 */
export function previewSchedule(run: () => Promise<void>, delayMs = 150, timers: Timers = globalTimers): PreviewSchedule {
  let handle: unknown = null;
  let waiters: (() => void)[] = [];
  let running: Promise<void> = Promise.resolve();
  const fire = (): void => {
    handle = null;
    const batch = waiters;
    waiters = [];
    running = running.then(run).catch(() => undefined).finally(() => { for (const w of batch) w(); });
  };
  return {
    schedule: () => new Promise<void>((resolve) => {
      waiters.push(resolve);
      if (handle !== null) timers.clearTimeout(handle);
      handle = timers.setTimeout(fire, delayMs);
    }),
    cancel: () => {
      if (handle !== null) timers.clearTimeout(handle);
      handle = null;
      for (const w of waiters) w();
      waiters = [];
    },
  };
}
